"use client";

import { useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase/client";
import { posthog } from "@/lib/posthog";
import { ANALYTICS_EVENTS } from "@bash/shared";

/**
 * useConvergingRead — the ONE wake-correct data rail for board surfaces.
 *
 * Every "server row IS the card" hook (interactions, proposals, brief, digest,
 * prep pack, routine report) needs the same machinery, and each private copy
 * of it re-earned the same bug: the desktop webview's runtime freezes while
 * occluded (App Nap / WebKit WebContent suspension), its sockets die, and the
 * first reads after wake fire into a half-resumed network stack and fail or
 * hang. A one-shot fetch then paints (or clobbers) the board wrong until the
 * user happens to poke it — the "needs-you cards show up minutes late" class,
 * cured for interactions in #1932/#1934 and extracted here for every surface.
 *
 * What the rail owns (the convergence law, applied to UI):
 * - LEVEL-TRIGGERED READS: a failed read re-asks — 250ms first (cold-launch
 *   token race), then doubling to a 10s ceiling — and never rests until a
 *   read SUCCEEDS. Push (realtime) is the optimization, never the only path.
 * - FAILED READ = UNKNOWN: an errored/hung read never reaches `apply`, so it
 *   can never clobber a populated board with an error's empty result.
 * - HUNG READS ABORT: 8s AbortSignal.timeout per attempt — a socket that died
 *   while napped becomes a fast retry instead of a silent 20s+ stall.
 * - WAKE TRIGGERS: visibilitychange, window focus (webviews skip
 *   visibilitychange on restore), window online (the network stack actually
 *   returning), auth settle (INITIAL_SESSION / SIGNED_IN / TOKEN_REFRESHED —
 *   heals the RLS-empty read that "succeeds" with zero rows), and channel
 *   SUBSCRIBED (first join AND every rejoin re-reads what the dead wire missed).
 * - TRIGGER COALESCING: triggers landing mid-read queue into ONE follow-up
 *   read (never dropped — the in-flight snapshot may predate the trigger).
 * - ONE READ PER RETURN: a return to the tab arrives as visibilitychange AND
 *   focus; the second announcement (inside RETURN_COALESCE_MS) is answered by
 *   the read the first one started. Audit 09-24: every home list read 2-4x
 *   on each return.
 * - NO POLL WHILE HIDDEN: the heartbeat and the retry ladder skip while the
 *   tab is hidden (realtime keeps pushing); the return reads once.
 * - ONE RAIL PER KEY: components asking for the same rows (same table, key,
 *   user) share one rail: one read, one beat, one channel, and every member
 *   gets the rows and the pushes.
 * - THE ARRIVAL PROBE: every read reports rows/errored/had_token/trigger/
 *   attempt/error to PostHog (keyed by `table`), so "the board arrived wrong"
 *   diagnoses itself from telemetry instead of screenshots.
 */

/** Supabase postgres_changes payload, passed through verbatim to handlers. */
export interface ChangePayload {
  new: unknown;
  old?: unknown;
}

export interface ConvergingChange {
  event: "INSERT" | "UPDATE" | "DELETE";
  handler: (payload: ChangePayload) => void;
}

export interface ConvergingReadSpec<TRow> {
  /** A value that changes when the caller knows the world moved (a message you
   *  just sent): the read re-runs at once instead of waiting for the beat. */
  wake?: string | number | null;
  /** Postgres table — also the channel topic prefix (`table:userId`) and probe key. */
  table: string;
  /**
   * Rail identity for PARAMETERIZED reads (e.g. one rail per task over one
   * table). Names the effect lifecycle, the channel topic, and the cache key —
   * so changing it tears down and re-converges. The probe key stays `table`
   * (low cardinality). Defaults to `table`, which is byte-identical to the
   * pre-key behavior every board consumer relies on.
   */
  key?: string;
  /**
   * Local-first paint opt-out. `false` skips the localStorage cache entirely —
   * for per-entity rails whose keys would otherwise accumulate unbounded.
   * Defaults to cached (the board behavior).
   */
  cache?: boolean;
  userId: string | null;
  /**
   * One source-of-truth read. Attach `signal` to the query via
   * `.abortSignal(signal)` and THROW on a Supabase error — resolving means
   * the returned rows are the truth.
   */
  read: (signal: AbortSignal) => Promise<TRow[]>;
  /** Apply a successful read wholesale (the read wins; deltas are hints). */
  apply: (rows: TRow[]) => void;
  /** Realtime bindings on `table`, filtered to this user. */
  changes: ConvergingChange[];
}

/** How long one return lasts: the browser announces a return to the tab as a
 *  visibilitychange and then a window focus (a webview may send either alone),
 *  and a second announcement inside this window is the same return. */
export const RETURN_COALESCE_MS = 2_000;
const HEARTBEAT_MS = 30_000;

function isHidden(): boolean {
  return (
    typeof document !== "undefined" && document.visibilityState === "hidden"
  );
}

/** One component reading a rail: its latest spec and its own loading flag. */
interface Member {
  spec: { current: ConvergingReadSpec<unknown> };
  setLoading: (loading: boolean) => void;
}

interface Rail {
  refetch: (trigger: string) => void;
  join: (member: Member) => void;
  leave: (member: Member) => void;
}

/**
 * Live rails by identity. Two components asking for the same rows (the home
 * board and the Dock both read `interactions`) share ONE rail: one read per
 * trigger, one beat, one channel. Two rails on one key also meant two
 * `supabase.channel()` calls on one topic, which hands back the SAME channel,
 * so the first to unmount removed the other's realtime from under it.
 */
const rails = new Map<string, Rail>();

function openRail(
  id: string,
  cfg: {
    table: string;
    railKey: string;
    userId: string;
    useCache: boolean;
    changes: ConvergingChange[];
  }
): Rail {
  const { table, railKey, userId, useCache } = cfg;
  const members = new Set<Member>();
  const each = (fn: (m: Member) => void) => [...members].forEach(fn);

  // LOCAL-FIRST PAINT (founder 07-16, "native apps never blip"): the last
  // successful read persists per user+table; paint it INSTANTLY at mount so
  // no pixel ever waits on the network — the converging read then diffs
  // truth in behind it (unchanged board = zero DOM mutations; a real change
  // moves like news). The cache is a first-paint HINT only: failed reads
  // still never clobber, and every successful read overwrites it. `last` is
  // also what a component joining a live rail paints first.
  const cacheKey = `vestra:board-cache:${railKey}:${userId}`;
  let last: unknown[] | null = null;
  if (useCache) {
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached !== null) last = JSON.parse(cached) as unknown[];
    } catch {
      // Corrupt or unavailable storage — cold paint; the network fills it.
    }
  }

  let disposed = false;
  let inFlight = false;
  let queuedTrigger: string | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;

  const readOnce = async (
    trigger: string,
    attempt: number
  ): Promise<boolean> => {
    // One key names one query: the oldest member reads for all of them.
    const reader = members.values().next().value;
    if (!reader) return true;
    let rows: unknown[] | null = null;
    let errorCode: string | null = null;
    let errorMessage: string | null = null;
    try {
      rows = await reader.spec.current.read(AbortSignal.timeout(8_000));
    } catch (e) {
      const err = e as { code?: string; message?: string };
      errorCode = err?.code || "exception";
      errorMessage = err?.message ?? String(e);
    }
    // Session read is in-memory, sub-ms.
    const { data: sessionData } = await supabase.auth.getSession();
    // Healthy steady-state heartbeats stay silent; anything errored or
    // retried still reports, so incidents keep naming themselves.
    const silent =
      trigger === "heartbeat" && attempt === 0 && errorCode === null;
    if (!silent)
      posthog.capture(ANALYTICS_EVENTS.HOME_NEEDS_ARRIVAL, {
        key: table,
        rows: rows?.length ?? 0,
        errored: errorCode !== null,
        had_token: Boolean(sessionData?.session),
        trigger,
        attempt,
        error_code: errorCode,
        error_message: errorMessage,
      });
    if (errorCode !== null) {
      // UNKNOWN, not "empty" — never clobber; loading still resolves so the
      // UI never hangs on a spinner while the ladder converges.
      each((m) => m.setLoading(false));
      return false;
    }
    if (!disposed) {
      const truth = rows ?? [];
      last = truth;
      each((m) => m.spec.current.apply(truth));
      if (useCache) {
        try {
          localStorage.setItem(cacheKey, JSON.stringify(truth));
        } catch {
          // Quota/private mode — the paint still happened; only the next
          // cold mount loses its head start.
        }
      }
    }
    each((m) => m.setLoading(false));
    return true;
  };

  const refetch = (trigger: string, attempt = 0) => {
    if (disposed) return;
    if (inFlight) {
      queuedTrigger = trigger;
      return;
    }
    if (retryTimer !== null) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
    inFlight = true;
    void readOnce(trigger, attempt)
      .catch(() => false)
      .then((ok) => {
        inFlight = false;
        if (disposed) return;
        if (queuedTrigger !== null) {
          const next = queuedTrigger;
          queuedTrigger = null;
          refetch(next);
          return;
        }
        if (ok) return;
        const delay =
          attempt === 0 ? 250 : Math.min(2000 * 2 ** attempt, 10_000);
        retryTimer = setTimeout(() => {
          retryTimer = null;
          // A hidden tab climbs no ladder: the return re-reads at once.
          if (isHidden()) return;
          refetch(trigger, attempt + 1);
        }, delay);
      });
  };

  // HEARTBEAT (founder incident 07-16 ×2: dead keepalive pools hung every
  // read for ~2.5min; even Cmd+R can't flush them — the pool lives in the
  // webview's network process). A steady beat keeps sockets exercised so
  // idle NAT/LB drops can't corpse them silently, and discovers a corpse
  // within one beat instead of TCP's multi-minute funeral. Healthy beats
  // skip the probe (see readOnce) so telemetry stays signal. NO BEAT WHILE
  // HIDDEN: nobody is looking, realtime still pushes, and the return is a
  // wake that reads once.
  const heartbeat = setInterval(() => {
    if (!isHidden()) refetch("heartbeat");
  }, HEARTBEAT_MS);

  // ONE READ PER RETURN: the browser announces a return twice (visibilitychange,
  // then focus), and the second announcement inside RETURN_COALESCE_MS is the
  // same return, so it is not announced to the rail at all. The gate sits
  // HERE, on the announcement, never in refetch: refetch stays the engine, so
  // a return it queued behind an in-flight read always reads when dequeued,
  // and a failed read always arms the ladder.
  let lastReturnAt = -Infinity;
  const announceReturn = (trigger: "visibility" | "focus") => {
    const now = Date.now();
    if (now - lastReturnAt < RETURN_COALESCE_MS) return;
    lastReturnAt = now;
    refetch(trigger);
  };
  const onVisibility = () => {
    if (document.visibilityState === "visible") announceReturn("visibility");
  };
  document.addEventListener("visibilitychange", onVisibility);
  const onFocus = () => announceReturn("focus");
  window.addEventListener("focus", onFocus);
  const onOnline = () => refetch("online");
  window.addEventListener("online", onOnline);

  const { data: authSub } = supabase.auth.onAuthStateChange((event) => {
    if (
      event === "INITIAL_SESSION" ||
      event === "SIGNED_IN" ||
      event === "TOKEN_REFRESHED"
    ) {
      refetch(`auth:${event}`);
    }
    if (event === "SIGNED_OUT") {
      try {
        localStorage.removeItem(cacheKey);
      } catch {
        // noop — worst case the key dies with the browser profile.
      }
    }
  });

  let channel = supabase.channel(`${railKey}:${userId}`);
  for (const event of new Set(cfg.changes.map((c) => c.event))) {
    channel = channel.on(
      "postgres_changes",
      {
        event,
        schema: "public",
        table,
        filter: `user_id=eq.${userId}`,
      },
      (payload) => {
        if (disposed) return;
        // Every member folds the push into its own rows, through its
        // latest handlers (read via the ref, so never stale).
        each((m) =>
          m.spec.current.changes.forEach((c) => {
            if (c.event === event) c.handler(payload as ChangePayload);
          })
        );
      }
    );
  }
  channel.subscribe((status) => {
    if (status === "SUBSCRIBED") refetch("subscribed");
  });

  return {
    refetch: (trigger) => refetch(trigger),
    join: (member) => {
      members.add(member);
      if (last !== null) {
        member.spec.current.apply(last);
        member.setLoading(false);
      }
      // Every component that mounts asks for fresh truth; landing mid-read
      // it queues the one follow-up read.
      refetch("mount");
    },
    leave: (member) => {
      members.delete(member);
      if (members.size > 0) return;
      disposed = true;
      rails.delete(id);
      clearInterval(heartbeat);
      if (retryTimer !== null) clearTimeout(retryTimer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onOnline);
      authSub.subscription.unsubscribe();
      void supabase.removeChannel(channel);
    },
  };
}

export function useConvergingRead<TRow>(spec: ConvergingReadSpec<TRow>): {
  loading: boolean;
} {
  const { userId, table } = spec;
  const railKey = spec.key ?? table;
  const useCache = spec.cache !== false;
  // Logged-out viewers never fetch, so start resolved (no stuck spinner).
  const [loading, setLoading] = useState(userId != null);

  // Latest-ref: read/apply/handlers are per-render closures; the effect keys
  // on the rail identity only and the rail always calls through the ref, so
  // handlers never go stale and the channel never churns on re-renders.
  const specRef = useRef(spec);
  specRef.current = spec;
  const refetchRef = useRef<((trigger: string) => void) | null>(null);
  const wake = spec.wake;
  useEffect(() => {
    // The mount read runs from the main effect; a wake only matters once it changes.
    if (wake == null) return;
    refetchRef.current?.("wake");
  }, [wake]);

  useEffect(() => {
    if (!userId) {
      refetchRef.current = null; // signed out: a later wake has nothing to call
      setLoading(false);
      return;
    }
    setLoading(true);
    const id = JSON.stringify([table, railKey, userId, useCache]);
    let rail = rails.get(id);
    if (!rail) {
      rail = openRail(id, {
        table,
        railKey,
        userId,
        useCache,
        changes: specRef.current.changes,
      });
      rails.set(id, rail);
    }
    const member: Member = {
      spec: specRef as unknown as Member["spec"],
      setLoading,
    };
    rail.join(member);
    refetchRef.current = rail.refetch;
    const joined = rail;
    return () => joined.leave(member);
  }, [userId, table, railKey, useCache]);

  return { loading };
}
